import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import Pagination from '../../components/common/Pagination';
import { fetchRoleGroups, fetchPermissions, updateRoleGroup, deleteRoleGroup } from '../../services/api';
import './RoleManagementPage.css';

function RoleManagementPage() {
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const [selectedRole, setSelectedRole] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 수정 모드 관련 state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    code: '',
    description: '',
    permissionIds: []
  });

  // 전체 권한 목록
  const [allPermissions, setAllPermissions] = useState([]);

  // API에서 권한 그룹 목록 및 전체 권한 목록 가져오기
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [roleGroupsData, permissionsData] = await Promise.all([
          fetchRoleGroups(),
          fetchPermissions()
        ]);
        
        
        setRoles(roleGroupsData);
        setAllPermissions(permissionsData);
      } catch (err) {
        console.error('데이터 조회 실패:', err);
        alert('데이터를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // 필터링된 권한 그룹
  const filteredRoles = useMemo(() => {
    return roles.filter(role =>
      role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [roles, searchTerm]);

  // 페이지네이션 적용
  const paginatedRoles = useMemo(() => {
    const startIndex = page * size;
    return filteredRoles.slice(startIndex, startIndex + size);
  }, [filteredRoles, page, size]);

  const totalElements = filteredRoles.length;
  const totalPages = Math.ceil(totalElements / size);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSizeChange = (newSize) => {
    setSize(newSize);
    setPage(0);
  };

  // 검색어 변경 시 첫 페이지로
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setIsEditMode(false);
    setEditForm({
      name: role.name,
      code: role.code,
      description: role.description || '',
      permissionIds: role.permissions ? role.permissions.map(p => p.id) : []
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRole(null);
    setIsEditMode(false);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditForm({
      name: selectedRole.name,
      code: selectedRole.code,
      description: selectedRole.description || '',
      permissionIds: selectedRole.permissions ? selectedRole.permissions.map(p => p.id) : []
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionToggle = (permissionId) => {
    setEditForm(prev => {
      const isSelected = prev.permissionIds.includes(permissionId);
      return {
        ...prev,
        permissionIds: isSelected
          ? prev.permissionIds.filter(id => id !== permissionId)
          : [...prev.permissionIds, permissionId]
      };
    });
  };

  const handleUpdateSubmit = async () => {
    try {
      const updatedRoleGroup = await updateRoleGroup(selectedRole.id, editForm);
      

      // 목록 새로고침
      const roleGroupsData = await fetchRoleGroups();
      setRoles(roleGroupsData);

      // 모달 업데이트
      setSelectedRole(updatedRoleGroup);
      setIsEditMode(false);
      alert('권한 그룹이 수정되었습니다.');
    } catch (error) {
      console.error('권한 그룹 수정 실패:', error);
      alert('권한 그룹 수정에 실패했습니다.');
    }
  };

  const handleDeleteClick = (role) => {
    setRoleToDelete(role);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteRoleGroup(roleToDelete.id);
      

      // 목록에서 제거
      setRoles(roles.filter(r => r.id !== roleToDelete.id));
      setIsDeleteModalOpen(false);
      setRoleToDelete(null);

      alert(`"${roleToDelete.name}" 권한 그룹이 삭제되었습니다.`);
    } catch (error) {
      console.error('권한 그룹 삭제 실패:', error);
      alert('권한 그룹 삭제에 실패했습니다. 해당 권한 그룹을 사용하는 사용자가 있을 수 있습니다.');
    }
  };

  // 권한을 카테고리별로 그룹화
  const groupPermissionsByCategory = (permissions) => {
    if (!permissions || permissions.length === 0) return {};
    return permissions.reduce((acc, perm) => {
      const category = perm.category || '기타';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(perm);
      return acc;
    }, {});
  };

  if (loading) {
    return (
      <MainLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
          데이터를 불러오는 중...
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="role-management-container">
        {/* Header Section */}
        <div className="role-management-header">
          <div className="role-management-header-left">
            <div className="role-management-title">권한 관리</div>
            <p className="role-management-subtitle">사용자 권한 및 접근 제어 관리</p>
          </div>
          <div className="role-management-header-right">
            {/* Optional update info */}
          </div>
        </div>

        <div className="role-management-content">
          {/* Controls Section */}
          <div className="role-controls">
            <div className="control-left">
              {/* Optional filters can go here */}
            </div>
            <div className="control-right">
              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="권한명, 권한코드 검색..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <span className="material-symbols-outlined search-icon">search</span>
              </div>
              <button className="btn-add-role" onClick={() => navigate('/role-registration')}>
                <svg className="plus-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                권한 추가
              </button>
            </div>
          </div>

          <div className="role-table-container">
            <table className="role-table">
            <thead>
              <tr>
                <th>권한명</th>
                <th>권한코드</th>
                <th>권한 수</th>
                <th>권한 범위</th>
                <th>설명</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRoles.map((role) => (
                <tr key={role.id} onClick={() => handleRoleClick(role)}>
                  <td>
                    <div className="role-name">
                      <span className="material-symbols-outlined">shield</span>
                      {role.name}
                    </div>
                  </td>
                  <td><span className="role-code">{role.code}</span></td>
                  <td>{role.permissions?.length || 0}개</td>
                  <td>
                    <div className="permission-badges">
                      {role.permissions?.slice(0, 3).map((perm) => (
                        <span key={perm.id} className="permission-badge">
                          {perm.name}
                        </span>
                      ))}
                      {role.permissions && role.permissions.length > 3 && (
                        <span className="permission-badge more">
                          +{role.permissions.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{role.description || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" onClick={(e) => { e.stopPropagation(); }}>
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                      <button className="btn-icon" onClick={(e) => { e.stopPropagation(); handleDeleteClick(role); }}>
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalElements > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalElements={totalElements}
              size={size}
              onPageChange={handlePageChange}
              onSizeChange={handleSizeChange}
            />
          )}
          </div>
        </div>

        {/* 권한 상세 모달 */}
        {isModalOpen && selectedRole && (
          <>
            <div className="modal-overlay" onClick={handleCloseModal}></div>
            <div className="role-detail-modal">
              <div className="modal-header">
                <h3>{isEditMode ? '권한 그룹 수정' : '권한 그룹 상세정보'}</h3>
                <button className="btn-close" onClick={handleCloseModal}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="modal-content">
                <div className="detail-section">
                  <label>권한 그룹명</label>
                  {isEditMode ? (
                    <input
                      type="text"
                      name="name"
                      value={editForm.name}
                      onChange={handleInputChange}
                      className="edit-input"
                      placeholder="권한 그룹명을 입력하세요"
                    />
                  ) : (
                    <div className="detail-value">{selectedRole.name}</div>
                  )}
                </div>
                <div className="detail-section">
                  <label>권한코드</label>
                  {isEditMode ? (
                    <input
                      type="text"
                      name="code"
                      value={editForm.code}
                      onChange={handleInputChange}
                      className="edit-input"
                      placeholder="권한코드를 입력하세요"
                    />
                  ) : (
                    <div className="detail-value">{selectedRole.code}</div>
                  )}
                </div>
                <div className="detail-section">
                  <label>설명</label>
                  {isEditMode ? (
                    <textarea
                      name="description"
                      value={editForm.description}
                      onChange={handleInputChange}
                      className="edit-textarea"
                      placeholder="설명을 입력하세요"
                      rows="3"
                    />
                  ) : (
                    <div className="detail-value">{selectedRole.description || '-'}</div>
                  )}
                </div>
                <div className="detail-section">
                  <label>
                    권한 목록
                    {isEditMode
                      ? ` (${editForm.permissionIds.length}개 선택)`
                      : ` (${selectedRole.permissions?.length || 0}개)`
                    }
                  </label>
                  <div className="permissions-tree-modal">
                    {isEditMode ? (
                      // 수정 모드: 전체 권한을 카테고리별로 표시하고 체크박스로 선택
                      Object.entries(groupPermissionsByCategory(allPermissions)).map(([category, perms]) => (
                        <div key={category} className="permission-category-modal">
                          <div className="category-header-modal">
                            <span className="category-icon-modal">📁</span>
                            <span className="category-name-modal">{category}</span>
                            <span className="permission-count-modal">({perms.length})</span>
                          </div>
                          <div className="category-permissions-modal">
                            {perms.map((perm) => (
                              <div
                                key={perm.id}
                                className={`permission-item-modal selectable ${editForm.permissionIds.includes(perm.id) ? 'selected' : ''}`}
                                onClick={() => handlePermissionToggle(perm.id)}
                              >
                                <input
                                  type="checkbox"
                                  checked={editForm.permissionIds.includes(perm.id)}
                                  onChange={() => handlePermissionToggle(perm.id)}
                                  className="permission-checkbox"
                                />
                                <span className="permission-label-modal">{perm.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      // 읽기 모드: 선택된 권한만 표시
                      selectedRole.permissions && selectedRole.permissions.length > 0 ? (
                        Object.entries(groupPermissionsByCategory(selectedRole.permissions)).map(([category, perms]) => (
                          <div key={category} className="permission-category-modal">
                            <div className="category-header-modal">
                              <span className="category-icon-modal">📁</span>
                              <span className="category-name-modal">{category}</span>
                              <span className="permission-count-modal">({perms.length})</span>
                            </div>
                            <div className="category-permissions-modal">
                              {perms.map((perm) => (
                                <div key={perm.id} className="permission-item-modal granted">
                                  <span className="permission-icon-modal">✓</span>
                                  <span className="permission-label-modal">{perm.name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-permissions">권한이 설정되지 않았습니다.</div>
                      )
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                {isEditMode ? (
                  <>
                    <button className="btn-primary" onClick={handleUpdateSubmit}>저장</button>
                    <button className="btn-secondary" onClick={handleCancelEdit}>취소</button>
                  </>
                ) : (
                  <>
                    <button className="btn-primary" onClick={handleEditClick}>수정</button>
                    <button className="btn-secondary" onClick={handleCloseModal}>닫기</button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="권한 그룹 삭제"
        message={roleToDelete ? `"${roleToDelete.name}" 권한 그룹을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.` : ''}
        confirmText="삭제"
        cancelText="취소"
        type="danger"
      />
    </MainLayout>
  );
}

export default RoleManagementPage;
